/**
 * @file Catalogue de 24 skins (palettes reprises de la v1) avec bonus permanents actifs meme non equipes.
 */
import type { StatId } from '@shared/enums';
import { BALANCE } from './balance';
import type { HatId, OutfitId, SkinDef, SkinEconomy, WalletAmounts } from './types';

type Row = [id: string, en: string, fr: string, fur: string, outfit: OutfitId, outfitColor: string | undefined, hat: HatId, stripes?: string];

const ROWS: Row[] = [
  // heros par defaut en orange (complementaire du vert des forets) : on le repere tout de suite a l'ecran
  ['classic', 'Classic', 'Classique', '#ff9a2e', 'none', undefined, 'none'],
  ['smoky', 'Smoky', 'Fume', '#b9bcc9', 'scarf', '#e8625a', 'none', '#8d90a3'],
  ['ginger', 'Ginger', 'Roux', '#d9662b', 'shirt', '#c9423e', 'none'],
  ['tabby', 'Tabby', 'Tigre', '#d1b595', 'shirt', '#f0f0ea', 'none', '#7e6347'],
  ['bluey', 'Bluey', 'Bleute', '#a9b4c4', 'shirt', '#4f78c9', 'none'],
  ['cocoa', 'Cocoa', 'Cacao', '#a8794f', 'shirt', '#4f9a5a', 'none', '#5e4029'],
  ['shadow', 'Shadow', 'Ombre', '#5b5f6b', 'shirt', '#37466e', 'none', '#33363f'],
  ['maid', 'Maid', 'Soubrette', '#e0d7cf', 'apron', '#5b4a42', 'maid'],
  ['sailor', 'Sailor', 'Marin', '#c3ccd8', 'sailor', '#f3f3f0', 'sailor'],
  ['chef', 'Chef', 'Cuisinier', '#e9c590', 'apron', '#f6f6f0', 'chef'],
  ['nurse', 'Nurse', 'Infirmiere', '#e6d4cb', 'apron', '#f6f0f0', 'nurse'],
  ['police', 'Police', 'Policier', '#a9b4c4', 'shirt', '#35457a', 'police'],
  ['detective', 'Detective', 'Detective', '#c8a47a', 'cape', '#a07c50', 'detective', '#8a6a46'],
  ['witch', 'Witch', 'Sorciere', '#a79dc6', 'cape', '#4e3c86', 'witch'],
  ['princess', 'Princess', 'Princesse', '#eed7c0', 'dress', '#f3a9c4', 'tiara'],
  ['cowboy', 'Cowboy', 'Cow-boy', '#c79a63', 'scarf', '#d8493e', 'cowboy', '#8c6a3f'],
  ['captain', 'Captain', 'Capitaine', '#b0bbd0', 'shirt', '#2f4f9a', 'captain'],
  ['wizard', 'Wizard', 'Magicien', '#c1b6dc', 'cape', '#6a4fb8', 'wizard'],
  ['knight', 'Knight', 'Chevalier', '#a3a9b5', 'armor', '#b4bccb', 'helmet'],
  ['pirate', 'Pirate', 'Pirate', '#7d6248', 'shirt', '#bd3b3b', 'pirate', '#4d3a28'],
  ['bunny', 'Bunny', 'Lapin', '#f4ece6', 'shirt', '#f2a7b8', 'bunny'],
  ['beanie', 'Beanie', 'Bonnet', '#e8b878', 'scarf', '#58a9d8', 'beanie'],
  ['dj', 'DJ', 'DJ', '#6e6a78', 'shirt', '#f2c43a', 'headphones', '#45414e'],
  ['king', 'King', 'Roi', '#f3d28d', 'cape', '#c23a3a', 'crown'],
];

const ECO = BALANCE.skins;

/** Cout : gratuit (i = 0), Argent pour les `moneyCount` suivants, Gemmes pour les derniers (voir BALANCE.skins). */
export const skinCostOf = (i: number, eco: SkinEconomy = ECO): WalletAmounts => {
  if (i === 0) return {};
  if (i <= eco.moneyCount) return { money: Math.round(eco.moneyBase * Math.pow(eco.moneyGrowth, i - 1)) };
  const k = i - eco.moneyCount - 1;
  return { dia: Math.round((eco.diaBase * Math.pow(eco.diaGrowth, k)) / eco.diaRound) * eco.diaRound };
};

/** Palier de bonus : 1..5 pour les skins Argent (tous les `skinsPerTier`), puis +diaTierStep par skin Gemmes. */
export const skinTierOf = (i: number, eco: SkinEconomy = ECO): number => {
  if (i === 0) return 0;
  const moneyTier = (j: number): number => 1 + Math.floor((j - 1) / eco.skinsPerTier);
  if (i <= eco.moneyCount) return moneyTier(i);
  return moneyTier(eco.moneyCount) + eco.diaTierStep * (i - eco.moneyCount);
};

/** Bonus additifs (ajoutes a la base 1 de la stat, voir StatAggregator), cumules sur tous les skins possedes. */
const bonusOf = (i: number): SkinDef['bonus'] => {
  const tier = skinTierOf(i);
  if (tier === 0) return [];
  return (Object.keys(ECO.bonusPerTier) as StatId[]).map((stat) => ({ stat, op: 'add' as const, value: Math.round((ECO.bonusPerTier[stat] ?? 0) * tier * 1000) / 1000 }));
};

export const SKINS: readonly SkinDef[] = ROWS.map(
  ([id, en, fr, fur, outfit, outfitColor, hat, stripes], i): SkinDef => ({
    id,
    name: { en, fr },
    fur,
    stripes,
    outfit,
    outfitColor,
    hat,
    cost: skinCostOf(i),
    bonus: bonusOf(i),
  }),
);

export const SKIN_BY_ID: ReadonlyMap<string, SkinDef> = new Map(SKINS.map((s) => [s.id, s]));
export const DEFAULT_SKIN_ID = 'classic';
